/**
 * Product Page Enhanced - Modern E-commerce UX Patterns
 * 
 * Features:
 * - Dynamic variant switching with smooth transitions
 * - Real-time inventory updates 
 * - Progressive enhancement for accessibility
 * - Modern cart interactions
 * - Touch-optimized mobile experience
 * - Performance optimized with debouncing and lazy loading
 */

class ProductPageEnhanced {
  constructor() {
    this.product = null;
    this.currentVariant = null;
    this.sectionId = null;
    this.selectors = {
      container: '[data-section-type="product-info"]',
      form: 'form[data-type="add-to-cart-form"]',
      addButton: '[data-add-to-cart-button]',
      variantSelector: '[data-variant-selector]',
      variantInputs: '[data-variant-option]',
      priceContainer: '.price-container',
      stockIndicator: '.stock-indicator',
      quantityInput: '[data-quantity-input]',
      quantityButtons: '[data-quantity-change]',
      mediaGallery: '.product-media-gallery',
      breadcrumbs: '.breadcrumb-nav'
    };
    
    this.debounceTimers = new Map();
    this.isLoading = false;
    
    this.init();
  }

  /**
   * Initialize the product page enhanced functionality
   */
  init() {
    this.loadProductData();
    this.setupEventListeners();
    this.enhanceAccessibility();
    this.setupVariantHandling();
    this.setupQuantityControls();
    this.setupTouchOptimizations();
    this.setupProgressiveEnhancement();
    this.setupLooxReviewHiding();
    
    // Initialize with current variant
    if (this.currentVariant) {
      this.updateProductDisplay(this.currentVariant);
    }
    
    console.log('Product Page Enhanced initialized');
  }

  /**
   * Load product data from JSON script tags
   */
  loadProductData() {
    try {
      const container = document.querySelector(this.selectors.container);
      if (!container) return;
      
      this.sectionId = container.dataset.sectionId;
      
      // Load product JSON data
      const productScript = document.querySelector(`#ProductInfoJSON-${this.sectionId}`);
      if (productScript) {
        const data = JSON.parse(productScript.textContent);
        this.product = data.product;
        this.currentVariant = data.selected_variant;
      }
    } catch (error) {
      console.error('Failed to load product data:', error);
    }
  }

  /**
   * Setup all event listeners
   */
  setupEventListeners() {
    // Variant selection
    document.addEventListener('change', this.handleVariantChange.bind(this));
    
    // Quantity controls
    document.addEventListener('click', this.handleQuantityClick.bind(this));
    document.addEventListener('input', this.handleQuantityInput.bind(this));
    
    // Form submission
    document.addEventListener('submit', this.handleFormSubmit.bind(this));
    
    // Keyboard navigation
    document.addEventListener('keydown', this.handleKeyboardNavigation.bind(this));
    
    // Touch events for mobile
    if ('ontouchstart' in window) {
      this.setupTouchEvents();
    }
    
    // Progressive enhancement events
    window.addEventListener('load', this.handlePageLoad.bind(this));
    document.addEventListener('visibilitychange', this.handleVisibilityChange.bind(this));
  }

  /**
   * Handle variant selection changes
   */
  handleVariantChange(event) {
    const input = event.target;
    if (!input.matches(this.selectors.variantInputs)) return;
    
    this.debounce('variantChange', () => {
      this.updateSelectedVariant();
    }, 150);
  }

  /**
   * Update the selected variant based on current option selections
   */
  updateSelectedVariant() {
    if (!this.product) return;
    
    const form = document.querySelector(this.selectors.form);
    if (!form) return;
    
    const formData = new FormData(form);
    const selectedOptions = {};
    
    // Collect selected options
    this.product.options.forEach((option, index) => {
      const value = formData.get(`options[${option}]`);
      if (value) {
        selectedOptions[`option${index + 1}`] = value;
      }
    });
    
    // Find matching variant
    const variant = this.product.variants.find(v => {
      return Object.keys(selectedOptions).every(key => 
        v[key] === selectedOptions[key]
      );
    });
    
    if (variant && variant.id !== this.currentVariant?.id) {
      this.currentVariant = variant;
      this.updateProductDisplay(variant);
      this.updateURL(variant);
    }
  }

  /**
   * Update product display based on selected variant
   */
  updateProductDisplay(variant) {
    if (!variant) return;
    
    // Update price
    this.updatePrice(variant);
    
    // Update stock status
    this.updateStockStatus(variant);
    
    // Update add to cart button
    this.updateAddToCartButton(variant);
    
    // Update quantity max
    this.updateQuantityMax(variant);
    
    // Update variant selector
    this.updateVariantSelector(variant);
    
    // Update media gallery if needed
    this.updateMediaGallery(variant);
    
    // Trigger custom event
    this.dispatchVariantChangeEvent(variant);
  }

  /**
   * Update price display
   */
  updatePrice(variant) {
    const container = document.querySelector(this.selectors.priceContainer);
    if (!container) return;
    
    const currentPrice = container.querySelector('.price-current');
    const comparePrice = container.querySelector('.price-compare');
    const savings = container.querySelector('.price-savings');
    
    if (currentPrice) {
      currentPrice.textContent = this.formatMoney(variant.price);
      currentPrice.setAttribute('content', variant.price / 100);
    }
    
    // Handle compare at price
    if (comparePrice) {
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        comparePrice.textContent = this.formatMoney(variant.compare_at_price);
        comparePrice.style.display = 'inline';
        
        if (savings) {
          const savingsAmount = variant.compare_at_price - variant.price;
          const savingsPercent = Math.round((savingsAmount / variant.compare_at_price) * 100);
          savings.innerHTML = `
            <span class="savings-amount">Save ${this.formatMoney(savingsAmount)}</span>
            <span class="savings-percent">(${savingsPercent}% off)</span>
          `;
          savings.style.display = 'flex';
        }
      } else {
        comparePrice.style.display = 'none';
        if (savings) savings.style.display = 'none';
      }
    }
    
    // Update add to cart button price
    const addButton = document.querySelector(this.selectors.addButton);
    const buttonText = addButton?.querySelector('.btn-text');
    if (buttonText && variant.available) {
      buttonText.textContent = `Sepete Ekle - ${this.formatMoney(variant.price)}`;
    }
  }

  /**
   * Update stock status display
   */
  updateStockStatus(variant) {
    const indicator = document.querySelector(this.selectors.stockIndicator);
    if (!indicator) return;
    
    const status = indicator.querySelector('.stock-status');
    if (!status) return;
    
    // Remove existing classes
    status.className = 'stock-status';
    
    if (variant.available) {
      status.classList.add('stock-status--available');
      status.innerHTML = `
        <div>
          <svg class="stock-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
            <path d="M20 6L9 17l-5-5"></path>
          </svg>
          <span class="stock-text">Stokta Var</span>
        </div>
        ${this.getStockWarning(variant)}
        <span class="shipping-info">
          <svg class="shipping-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
            <polygon points="16,6 16,16 22,16 22,11 16,6"></polygon>
            <rect x="1" y="3" width="15" height="13"></rect>
            <circle cx="5.5" cy="18.5" r="2.5"></circle>
            <circle cx="18.5" cy="18.5" r="2.5"></circle>
          </svg>
          24 saat içinde kargoda
        </span>
      `;
    } else {
      status.classList.add('stock-status--unavailable');
      status.innerHTML = `
        <div>
          <svg class="stock-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="15" y1="9" x2="9" y2="15"></line>
            <line x1="9" y1="9" x2="15" y2="15"></line>
          </svg>
          <span class="stock-text">Tükendi</span>
        </div>
        <span class="restock-info">Tekrar stoklarda olduğunda bilgi al</span>
      `;
    }
    
    // Animate status change
    this.animateElement(indicator, 'pulse');
  }

  /**
   * Get stock warning HTML if applicable
   */
  getStockWarning(variant) {
    if (variant.inventory_quantity <= 10 && variant.inventory_management === 'shopify') {
      return `
        <span class="stock-warning stock-warning--urgent">
          <svg class="warning-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
            <path d="M12 9v3m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
          </svg>
          Son ${variant.inventory_quantity} adet!
        </span>
      `;
    }
    return '';
  }

  /**
   * Update add to cart button state
   */
  updateAddToCartButton(variant) {
    const button = document.querySelector(this.selectors.addButton);
    if (!button) return;
    
    const buttonText = button.querySelector('.btn-text');
    const buttonIcon = button.querySelector('.btn-icon');
    
    if (variant.available) {
      button.disabled = false;
      button.classList.remove('btn-sold-out');
      button.classList.add('btn-primary');
      
      if (buttonText) {
        buttonText.textContent = `Sepete Ekle - ${this.formatMoney(variant.price)}`;
      }
      
      if (buttonIcon) {
        buttonIcon.innerHTML = `
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="9" cy="21" r="1"></circle>
            <circle cx="20" cy="21" r="1"></circle>
            <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
          </svg>
        `;
      }
    } else {
      button.disabled = true;
      button.classList.remove('btn-primary');
      button.classList.add('btn-sold-out');
      
      if (buttonText) {
        buttonText.textContent = 'Tükendi';
      }
      
      if (buttonIcon) {
        buttonIcon.innerHTML = `
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="15" y1="9" x2="9" y2="15"></line>
            <line x1="9" y1="9" x2="15" y2="15"></line>
          </svg>
        `;
      }
    }
  }

  /**
   * Update quantity input max value
   */
  updateQuantityMax(variant) {
    const quantityInput = document.querySelector(this.selectors.quantityInput);
    if (!quantityInput) return;
    
    if (variant.inventory_management === 'shopify' && variant.inventory_quantity) {
      quantityInput.setAttribute('max', variant.inventory_quantity);
    } else {
      quantityInput.removeAttribute('max');
    }
  }

  /**
   * Update variant selector
   */
  updateVariantSelector(variant) {
    const select = document.querySelector(this.selectors.variantSelector);
    if (!select) return;
    
    select.value = variant.id;
  }

  /**
   * Update media gallery for variant
   */
  updateMediaGallery(variant) {
    // This would integrate with the media gallery component
    // For now, we'll just dispatch an event that the media gallery can listen to
    if (variant.featured_media?.id) {
      const event = new CustomEvent('variant:mediaChange', {
        detail: { variant, mediaId: variant.featured_media.id }
      });
      document.dispatchEvent(event);
    }
  }

  /**
   * Update URL with variant
   */
  updateURL(variant) {
    if (!window.history?.replaceState) return;
    
    const url = new URL(window.location);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({ variantId: variant.id }, '', url);
  }

  /**
   * Handle quantity control clicks
   */
  handleQuantityClick(event) {
    const button = event.target.closest(this.selectors.quantityButtons);
    if (!button) return;
    
    event.preventDefault();
    
    const change = parseInt(button.dataset.quantityChange);
    const input = button.parentNode.querySelector(this.selectors.quantityInput);
    
    if (input) {
      const currentValue = parseInt(input.value) || 1;
      const newValue = Math.max(1, currentValue + change);
      const max = input.getAttribute('max');
      
      if (!max || newValue <= parseInt(max)) {
        input.value = newValue;
        this.updateQuantityButtons(input);
        this.triggerInputEvent(input);
      }
    }
  }

  /**
   * Handle quantity input changes
   */
  handleQuantityInput(event) {
    const input = event.target;
    if (!input.matches(this.selectors.quantityInput)) return;
    
    this.debounce('quantityInput', () => {
      this.validateQuantity(input);
      this.updateQuantityButtons(input);
    }, 300);
  }

  /**
   * Validate quantity input
   */
  validateQuantity(input) {
    const value = parseInt(input.value) || 1;
    const min = parseInt(input.getAttribute('min')) || 1;
    const max = input.getAttribute('max');
    
    let newValue = Math.max(min, value);
    if (max && newValue > parseInt(max)) {
      newValue = parseInt(max);
      this.showQuantityError(input, `Maksimum ${max} adet seçebilirsiniz.`);
    }
    
    if (newValue !== value) {
      input.value = newValue;
    }
    
    this.clearQuantityError(input);
  }

  /**
   * Update quantity button states
   */
  updateQuantityButtons(input) {
    const container = input.parentNode;
    const decreaseBtn = container.querySelector('[data-quantity-change="-1"]');
    const increaseBtn = container.querySelector('[data-quantity-change="1"]');
    
    const value = parseInt(input.value) || 1;
    const min = parseInt(input.getAttribute('min')) || 1;
    const max = input.getAttribute('max');
    
    if (decreaseBtn) {
      decreaseBtn.disabled = value <= min;
    }
    
    if (increaseBtn && max) {
      increaseBtn.disabled = value >= parseInt(max);
    }
  }

  /**
   * Handle form submission
   */
  async handleFormSubmit(event) {
    const form = event.target;
    if (!form.matches(this.selectors.form)) return;
    
    event.preventDefault();
    
    if (this.isLoading) return;
    
    const button = form.querySelector(this.selectors.addButton);
    const formData = new FormData(form);
    
    try {
      this.setLoadingState(button, true);
      
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData
      });
      
      if (response.ok) {
        const item = await response.json();
        this.handleAddToCartSuccess(item, button);
      } else {
        const error = await response.json();
        this.handleAddToCartError(error.description || 'Bir hata oluştu', button);
      }
    } catch (error) {
      this.handleAddToCartError('Ağ hatası oluştu', button);
    } finally {
      this.setLoadingState(button, false);
    }
  }

  /**
   * Set loading state for button
   */
  setLoadingState(button, isLoading) {
    this.isLoading = isLoading;
    
    if (isLoading) {
      button.setAttribute('data-loading', 'true');
      button.disabled = true;
    } else {
      button.removeAttribute('data-loading');
      button.disabled = !this.currentVariant?.available;
    }
  }

  /**
   * Handle successful add to cart
   */
  handleAddToCartSuccess(item, button) {
    // Show success animation
    this.animateElement(button, 'success');
    
    // Update cart count if element exists
    this.updateCartCount();
    
    // Show success message
    this.showToast('Ürün sepete eklendi!', 'success');
    
    // NEW: Trigger cart drawer update (like product-card-neo.js)
    this.triggerCartDrawerUpdate(item);
    
    // Dispatch custom event
    const event = new CustomEvent('cart:item-added', {
      detail: { item }
    });
    document.dispatchEvent(event);
  }

  /**
   * Enhanced cart drawer update with comprehensive debugging (from product-card-neo.js)
   */
  async triggerCartDrawerUpdate(addToCartResponse) {
    console.log('🔧 [PRODUCT PAGE] Starting cart drawer update...', addToCartResponse);
    
    try {
      // First update cart icon bubble count immediately
      this.updateCartIconCount(addToCartResponse);
      
      // Check if cart drawer exists before proceeding
      const cartDrawer = document.querySelector('cart-drawer');
      console.log('🔧 [PRODUCT PAGE] Cart drawer element found:', !!cartDrawer);
      
      if (!cartDrawer) {
        console.warn('⚠️ [PRODUCT PAGE] Cart drawer element not found, skipping drawer update');
        // Show fallback notification since item was added successfully
        this.showFallbackNotification();
        return;
      }
      
      // Check if cart drawer is properly initialized
      if (typeof cartDrawer.renderContents !== 'function') {
        console.error('❌ [PRODUCT PAGE] Cart drawer renderContents method not available');
        this.showFallbackNotification();
        return;
      }
      
      // Add small delay to ensure cart drawer is fully ready
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Fetch fresh cart sections for cart drawer
      const sectionsToUpdate = ['cart-drawer'];
      const sectionsUrl = `${window.location.pathname}?sections=${sectionsToUpdate.join(',')}`;
      
      console.log('🔧 [PRODUCT PAGE] Fetching sections from:', sectionsUrl);
      
      const response = await fetch(sectionsUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch sections: ${response.status}`);
      }
      
      const sections = await response.json();
      console.log('🔧 [PRODUCT PAGE] Sections received:', Object.keys(sections));
      
      // Validate section content
      if (!sections['cart-drawer']) {
        console.error('❌ [PRODUCT PAGE] cart-drawer section missing from response');
        this.showFallbackNotification();
        return;
      }
      
      // Create a parsedState object that cart drawer expects
      const parsedState = {
        sections: sections
      };
      
      console.log('🔧 [PRODUCT PAGE] Calling cartDrawer.renderContents with:', parsedState);
      
      // Update cart drawer
      cartDrawer.renderContents(parsedState);
      
      // Also trigger standard cart events for other components
      document.dispatchEvent(new CustomEvent('cart:updated', {
        detail: { item: addToCartResponse }
      }));
      
      // Dispatch custom event to notify other components
      document.dispatchEvent(new CustomEvent('cart:drawer:updated', {
        detail: { 
          source: 'product-page',
          sections: sections,
          item: addToCartResponse 
        }
      }));
      
      console.log('✅ [PRODUCT PAGE] Cart drawer updated successfully');
      
    } catch (error) {
      console.error('❌ [PRODUCT PAGE] Failed to update cart drawer:', error);
      
      // Fallback: At least update the cart count and show fallback notification
      this.updateCartIconCount(addToCartResponse);
      this.showFallbackNotification();
    }
  }

  /**
   * Fallback notification when drawer update fails but item was added
   */
  showFallbackNotification() {
    const notification = document.createElement('div');
    notification.className = 'product-page-cart-fallback-notification';
    notification.innerHTML = `
      <div class="product-page-cart-fallback-content">
        <span>✅ Ürün sepete eklendi!</span>
        <button onclick="this.parentElement.parentElement.remove()" style="margin-left: 10px; background: none; border: none; font-size: 18px; cursor: pointer;">×</button>
      </div>
    `;
    
    // Add styles
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      z-index: 10000;
      background: #28a745;
      color: white;
      padding: 15px 20px;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.2);
      animation: slideInRight 0.3s ease-out;
    `;
    
    document.body.appendChild(notification);
    
    // Auto remove after 3 seconds
    setTimeout(() => {
      if (notification.parentElement) {
        notification.remove();
      }
    }, 3000);
  }

  /**
   * Helper method to update cart icon count immediately
   */
  updateCartIconCount(cartData) {
    try {
      // Fetch fresh cart data to get accurate count
      fetch('/cart.js')
        .then(response => response.json())
        .then(freshCartData => {
          const cartBubble = document.querySelector('.cart-count-bubble');
          if (cartBubble) {
            const countElement = cartBubble.querySelector('span[aria-hidden="true"]');
            if (countElement) {
              countElement.textContent = freshCartData.item_count;
            }
            cartBubble.style.display = freshCartData.item_count > 0 ? 'flex' : 'none';
          }
        })
        .catch(error => console.error('Error updating cart count:', error));
    } catch (error) {
      console.error('Error in updateCartIconCount:', error);
    }
  }

  /**
   * Handle add to cart error
   */
  handleAddToCartError(message, button) {
    this.showCartError(message);
    this.animateElement(button, 'error');
  }

  /**
   * Setup touch optimizations for mobile
   */
  setupTouchOptimizations() {
    if (!('ontouchstart' in window)) return;
    
    // Add touch classes
    document.body.classList.add('touch-device');
    
    // Improve touch targets
    const touchTargets = document.querySelectorAll('.variant-option, .quantity-button, .btn');
    touchTargets.forEach(target => {
      target.classList.add('touch-optimized');
    });
  }

  /**
   * Setup touch events
   */
  setupTouchEvents() {
    let touchStartY = 0;
    
    document.addEventListener('touchstart', (e) => {
      touchStartY = e.touches[0].clientY;
    }, { passive: true });
    
    document.addEventListener('touchmove', (e) => {
      const touchY = e.touches[0].clientY;
      const touchDiff = touchStartY - touchY;
      
      // Add pull-to-refresh prevention if needed
      if (touchDiff < 0 && window.scrollY === 0) {
        e.preventDefault();
      }
    }, { passive: false });
  }

  /**
   * Enhance accessibility
   */
  enhanceAccessibility() {
    // Add live region for dynamic content
    this.createLiveRegion();
    
    // Enhance variant options
    this.enhanceVariantAccessibility();
    
    // Setup keyboard navigation
    this.setupKeyboardNavigation();
  }

  /**
   * Create live region for announcements
   */
  createLiveRegion() {
    if (document.querySelector('#product-live-region')) return;
    
    const liveRegion = document.createElement('div');
    liveRegion.id = 'product-live-region';
    liveRegion.setAttribute('aria-live', 'polite');
    liveRegion.setAttribute('aria-atomic', 'true');
    liveRegion.className = 'sr-only';
    
    document.body.appendChild(liveRegion);
  }

  /**
   * Announce to screen readers
   */
  announce(message) {
    const liveRegion = document.querySelector('#product-live-region');
    if (liveRegion) {
      liveRegion.textContent = message;
      
      // Clear after announcement
      setTimeout(() => {
        liveRegion.textContent = '';
      }, 1000);
    }
  }

  /**
   * Enhanced variant accessibility
   */
  enhanceVariantAccessibility() {
    const variantInputs = document.querySelectorAll(this.selectors.variantInputs);
    
    variantInputs.forEach((input, index) => {
      const label = input.nextElementSibling;
      if (label) {
        // Add describedby for disabled variants
        if (input.disabled) {
          const disabledId = `variant-disabled-${index}`;
          let disabledMsg = document.querySelector(`#${disabledId}`);
          
          if (!disabledMsg) {
            disabledMsg = document.createElement('span');
            disabledMsg.id = disabledId;
            disabledMsg.className = 'sr-only';
            disabledMsg.textContent = 'Bu seçenek mevcut değil';
            label.appendChild(disabledMsg);
          }
          
          input.setAttribute('aria-describedby', disabledId);
        }
      }
    });
  }

  /**
   * Setup keyboard navigation
   */
  setupKeyboardNavigation() {
    // Arrow key navigation for variant options
    document.addEventListener('keydown', this.handleKeyboardNavigation.bind(this));
  }

  /**
   * Handle keyboard navigation
   */
  handleKeyboardNavigation(event) {
    const activeElement = document.activeElement;
    
    // Handle variant option navigation
    if (activeElement.matches(this.selectors.variantInputs)) {
      this.handleVariantKeyboard(event, activeElement);
    }
    
    // Handle quantity input
    if (activeElement.matches(this.selectors.quantityInput)) {
      this.handleQuantityKeyboard(event, activeElement);
    }
  }

  /**
   * Handle variant keyboard navigation
   */
  handleVariantKeyboard(event, input) {
    const container = input.closest('.variant-options');
    if (!container) return;
    
    const inputs = Array.from(container.querySelectorAll(this.selectors.variantInputs + ':not(:disabled)'));
    const currentIndex = inputs.indexOf(input);
    
    let targetIndex = currentIndex;
    
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        event.preventDefault();
        targetIndex = (currentIndex + 1) % inputs.length;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        event.preventDefault();
        targetIndex = currentIndex === 0 ? inputs.length - 1 : currentIndex - 1;
        break;
      case ' ':
      case 'Enter':
        event.preventDefault();
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        return;
    }
    
    if (targetIndex !== currentIndex && inputs[targetIndex]) {
      inputs[targetIndex].focus();
    }
  }

  /**
   * Handle quantity keyboard controls
   */
  handleQuantityKeyboard(event, input) {
    switch (event.key) {
      case 'ArrowUp':
        event.preventDefault();
        this.adjustQuantity(input, 1);
        break;
      case 'ArrowDown':
        event.preventDefault();
        this.adjustQuantity(input, -1);
        break;
    }
  }

  /**
   * Adjust quantity by amount
   */
  adjustQuantity(input, change) {
    const currentValue = parseInt(input.value) || 1;
    const newValue = Math.max(1, currentValue + change);
    const max = input.getAttribute('max');
    
    if (!max || newValue <= parseInt(max)) {
      input.value = newValue;
      this.updateQuantityButtons(input);
      this.triggerInputEvent(input);
    }
  }

  /**
   * Setup progressive enhancement
   */
  setupProgressiveEnhancement() {
    // Mark as enhanced
    document.documentElement.classList.add('product-page-enhanced');
    
    // Setup intersection observer for lazy loading
    if ('IntersectionObserver' in window) {
      this.setupLazyLoading();
    }
    
    // Setup connection awareness
    if ('connection' in navigator) {
      this.handleConnectionChange();
    }
  }

  /**
   * Setup lazy loading for non-critical elements
   */
  setupLazyLoading() {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('in-view');
          observer.unobserve(entry.target);
        }
      });
    });
    
    // Observe trust signals
    const trustSignals = document.querySelectorAll('.trust-signals-grid .trust-signal');
    trustSignals.forEach(signal => observer.observe(signal));
  }

  /**
   * Handle connection changes
   */
  handleConnectionChange() {
    const connection = navigator.connection;
    
    if (connection.effectiveType === 'slow-2g' || connection.effectiveType === '2g') {
      document.documentElement.classList.add('slow-connection');
      console.log('Slow connection detected, optimizing experience');
    }
  }

  /**
   * Handle page load
   */
  handlePageLoad() {
    // Analytics and performance tracking
    this.trackPageLoad();
    
    // Initialize any deferred features
    this.initializeDeferredFeatures();
  }

  /**
   * Handle visibility changes
   */
  handleVisibilityChange() {
    if (document.visibilityState === 'visible') {
      // Refresh inventory when page becomes visible
      this.debounce('inventoryRefresh', () => {
        this.refreshInventory();
      }, 1000);
    }
  }

  /**
   * Refresh inventory data
   */
  async refreshInventory() {
    if (!this.product?.handle || !this.currentVariant) return;
    
    try {
      const response = await fetch(`/products/${this.product.handle}.js`);
      if (response.ok) {
        const productData = await response.json();
        const variant = productData.variants.find(v => v.id === this.currentVariant.id);
        
        if (variant && variant.available !== this.currentVariant.available) {
          this.currentVariant = variant;
          this.updateProductDisplay(variant);
          
          if (variant.available) {
            this.announce('Bu ürün tekrar stokta!');
          }
        }
      }
    } catch (error) {
      console.error('Failed to refresh inventory:', error);
    }
  }

  /**
   * Utility Methods
   */

  /**
   * Debounce function calls
   */
  debounce(key, func, delay) {
    if (this.debounceTimers.has(key)) {
      clearTimeout(this.debounceTimers.get(key));
    }
    
    const timer = setTimeout(() => {
      func();
      this.debounceTimers.delete(key);
    }, delay);
    
    this.debounceTimers.set(key, timer);
  }

  /**
   * Format money using Shopify's format
   */
  formatMoney(cents) {
    const amount = cents / 100;
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY',
      minimumFractionDigits: 2
    }).format(amount);
  }

  /**
   * Animate element with CSS class
   */
  animateElement(element, animationType) {
    element.classList.add(`animate-${animationType}`);
    
    const cleanup = () => {
      element.classList.remove(`animate-${animationType}`);
      element.removeEventListener('animationend', cleanup);
    };
    
    element.addEventListener('animationend', cleanup);
  }

  /**
   * Show toast notification
   */
  showToast(message, type = 'info') {
    const toast = document.createElement('div');
    toast.className = `toast toast--${type}`;
    toast.textContent = message;
    
    document.body.appendChild(toast);
    
    setTimeout(() => {
      toast.classList.add('toast--visible');
    }, 10);
    
    setTimeout(() => {
      toast.classList.remove('toast--visible');
      setTimeout(() => {
        document.body.removeChild(toast);
      }, 300);
    }, 3000);
  }

  /**
   * Show cart error
   */
  showCartError(message) {
    const errorContainer = document.querySelector(`#cart-error-${this.sectionId}`);
    if (errorContainer) {
      errorContainer.textContent = message;
      errorContainer.classList.add('show');
      
      setTimeout(() => {
        errorContainer.classList.remove('show');
      }, 5000);
    }
  }

  /**
   * Show quantity error
   */
  showQuantityError(input, message) {
    const errorContainer = input.parentNode.parentNode.querySelector('.quantity-error');
    if (errorContainer) {
      errorContainer.textContent = message;
      errorContainer.classList.add('show');
    }
  }

  /**
   * Clear quantity error
   */
  clearQuantityError(input) {
    const errorContainer = input.parentNode.parentNode.querySelector('.quantity-error');
    if (errorContainer) {
      errorContainer.classList.remove('show');
    }
  }

  /**
   * Update cart count
   */
  async updateCartCount() {
    try {
      const response = await fetch('/cart.js');
      if (response.ok) {
        const cart = await response.json();
        const cartCount = document.querySelector('.cart-count');
        if (cartCount) {
          cartCount.textContent = cart.item_count;
        }
      }
    } catch (error) {
      console.error('Failed to update cart count:', error);
    }
  }

  /**
   * Trigger input event
   */
  triggerInputEvent(element) {
    element.dispatchEvent(new Event('input', { bubbles: true }));
  }

  /**
   * Dispatch variant change event
   */
  dispatchVariantChangeEvent(variant) {
    const event = new CustomEvent('variant:change', {
      detail: { 
        variant,
        product: this.product,
        sectionId: this.sectionId
      },
      bubbles: true
    });
    
    document.dispatchEvent(event);
    
    // Announce to screen readers
    this.announce(`${variant.title} seçildi. Fiyat: ${this.formatMoney(variant.price)}`);
  }

  /**
   * Track page load for analytics
   */
  trackPageLoad() {
    if (typeof gtag !== 'undefined' && this.product) {
      gtag('event', 'view_item', {
        currency: 'TRY',
        value: this.currentVariant?.price / 100,
        items: [{
          item_id: this.currentVariant?.sku || this.currentVariant?.id,
          item_name: this.product.title,
          item_category: this.product.type,
          item_variant: this.currentVariant?.title,
          price: this.currentVariant?.price / 100,
          quantity: 1
        }]
      });
    }
  }

  /**
   * Initialize deferred features
   */
  initializeDeferredFeatures() {
    // Initialize features that can wait until after page load
    this.setupAdvancedAnimations();
    this.setupPerformanceMonitoring();
  }

  /**
   * Setup advanced animations
   */
  setupAdvancedAnimations() {
    // Add scroll-based animations if user prefers motion
    if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.addScrollAnimations();
    }
  }

  /**
   * Add scroll animations
   */
  addScrollAnimations() {
    if ('IntersectionObserver' in window) {
      const animateOnScroll = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
          }
        });
      });

      const elements = document.querySelectorAll('.trust-signal, .detail-accordion');
      elements.forEach(el => animateOnScroll.observe(el));
    }
  }

  /**
   * Setup performance monitoring
   */
  setupPerformanceMonitoring() {
    if ('PerformanceObserver' in window) {
      try {
        const observer = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          entries.forEach(entry => {
            if (entry.entryType === 'largest-contentful-paint') {
              console.log('LCP:', entry.startTime);
            }
          });
        });

        observer.observe({ entryTypes: ['largest-contentful-paint'] });
      } catch (error) {
        console.warn('Performance monitoring not supported:', error);
      }
    }
  }

  /**
   * Setup Loox review form hiding functionality
   * This provides JavaScript fallback for any dynamically loaded review forms
   */
  setupLooxReviewHiding() {
    console.log('Setting up Loox review form hiding...');
    
    // Initial check for existing forms
    this.hideLooxReviewForms();
    
    // Set up MutationObserver to catch dynamically loaded forms
    if ('MutationObserver' in window) {
      const observer = new MutationObserver((mutations) => {
        let shouldCheck = false;
        
        mutations.forEach((mutation) => {
          // Check if nodes were added
          if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
            mutation.addedNodes.forEach((node) => {
              // Check if it's an element node
              if (node.nodeType === 1) {
                // Check if it's related to Loox or contains Loox elements
                if (this.isLooxRelatedElement(node)) {
                  shouldCheck = true;
                }
              }
            });
          }
        });
        
        if (shouldCheck) {
          // Debounce to avoid excessive checks
          this.debounce('looxFormHiding', () => {
            this.hideLooxReviewForms();
          }, 200);
        }
      });
      
      // Start observing the entire document
      observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: false
      });
      
      // Also specifically observe the Loox container if it exists
      const looxContainer = document.querySelector('#looxReviews');
      if (looxContainer) {
        observer.observe(looxContainer, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['class', 'style']
        });
      }
    }
    
    // Set up periodic checks as additional safety measure
    this.setupPeriodicLooxCheck();
    
    // Handle dynamic content loading events
    this.setupLooxEventHandlers();
  }

  /**
   * Check if an element is related to Loox
   */
  isLooxRelatedElement(element) {
    if (!element.classList && !element.className) return false;
    
    const className = element.className.toString();
    const looxClasses = [
      'loox', 'review', 'rating', 'write', 'submit', 'add',
      'comment', 'feedback', 'form', 'modal', 'overlay'
    ];
    
    return looxClasses.some(cls => className.includes(cls)) ||
           element.tagName === 'FORM' ||
           element.querySelector?.('.loox-write-review, .loox-review-form, .loox-add-review');
  }

  /**
   * Hide Loox review forms - main function
   */
  hideLooxReviewForms() {
    const looxContainer = document.querySelector('#looxReviews');
    if (!looxContainer) return;
    
    // Common Loox review writing selectors
    const reviewWritingSelectors = [
      '.loox-write-review',
      '.loox-review-form',
      '.loox-submit-review',
      '.loox-review-submission',
      '.loox-write-review-button',
      '.loox-add-review',
      '.loox-new-review',
      '.loox-review-composer',
      '.loox-rating-input',
      '.loox-star-input',
      '.loox-review-input',
      '.loox-review-textarea',
      '.loox-comment-form',
      '.loox-user-review-form',
      '.loox-customer-review-form',
      '.loox-submit-button',
      '.loox-post-review',
      '.loox-leave-review',
      '.loox-review-cta',
      '.loox-write-review-cta',
      '.loox-review-prompt',
      '.loox-rating-prompt',
      '.loox-review-overlay',
      '.loox-review-modal',
      '.loox-add-review-modal',
      '.loox-write-review-modal',
      '.loox-rating-form',
      '.loox-feedback-form',
      '[class*="write-review"]',
      '[class*="add-review"]',
      '[class*="submit-review"]',
      '[class*="review-form"]',
      '[class*="rating-input"]',
      '[data-action*="write"]',
      '[data-action*="submit"]',
      '[data-action*="add-review"]'
    ];
    
    let hiddenCount = 0;
    
    // Hide form elements
    reviewWritingSelectors.forEach(selector => {
      const elements = looxContainer.querySelectorAll(selector);
      elements.forEach(element => {
        if (element.style.display !== 'none') {
          element.style.setProperty('display', 'none', 'important');
          element.style.setProperty('visibility', 'hidden', 'important');
          element.style.setProperty('opacity', '0', 'important');
          element.style.setProperty('pointer-events', 'none', 'important');
          element.style.setProperty('height', '0', 'important');
          element.style.setProperty('overflow', 'hidden', 'important');
          element.setAttribute('aria-hidden', 'true');
          hiddenCount++;
        }
      });
    });
    
    // Hide interactive rating stars (for submitting ratings) while keeping display stars
    const interactiveStars = looxContainer.querySelectorAll('.loox-star-rating:not(.loox-review-card *)');
    interactiveStars.forEach(element => {
      // Only hide if it's not inside a review card (display stars)
      if (!element.closest('.loox-review-card')) {
        element.style.setProperty('display', 'none', 'important');
        element.style.setProperty('pointer-events', 'none', 'important');
        hiddenCount++;
      }
    });
    
    // Hide any buttons that might trigger review writing
    const reviewButtons = looxContainer.querySelectorAll(`
      button[onclick*="review"],
      button[onclick*="write"],
      button[onclick*="add"],
      button[data-action*="write-review"],
      button[data-action*="add-review"],
      a[href*="write"],
      a[href*="review"]:not([href*="reviews"]),
      .loox-cta-button:not(.loox-pagination-button)
    `);
    
    reviewButtons.forEach(button => {
      button.style.setProperty('display', 'none', 'important');
      button.style.setProperty('visibility', 'hidden', 'important');
      button.style.setProperty('pointer-events', 'none', 'important');
      button.setAttribute('aria-hidden', 'true');
      hiddenCount++;
    });
    
    // Hide any form inputs
    const formInputs = looxContainer.querySelectorAll('input, textarea, select');
    formInputs.forEach(input => {
      // Don't hide pagination form inputs
      if (!input.closest('.loox-pagination-form')) {
        input.style.setProperty('display', 'none', 'important');
        input.style.setProperty('visibility', 'hidden', 'important');
        hiddenCount++;
      }
    });
    
    // Hide forms (but not pagination forms)
    const forms = looxContainer.querySelectorAll('form:not(.loox-pagination-form)');
    forms.forEach(form => {
      form.style.setProperty('display', 'none', 'important');
      form.style.setProperty('visibility', 'hidden', 'important');
      form.style.setProperty('height', '0', 'important');
      form.style.setProperty('overflow', 'hidden', 'important');
      form.setAttribute('aria-hidden', 'true');
      hiddenCount++;
    });
    
    // Ensure review display elements remain visible
    const reviewDisplaySelectors = [
      '.loox-review-card',
      '.loox-review-text',
      '.loox-reviewer-name',
      '.loox-review-date',
      '.loox-review-content',
      '.loox-review-header',
      '.loox-verified-badge',
      '.loox-pagination-button',
      '.loox-reviews-summary',
      '.loox-average-rating',
      '.loox-review-list',
      '.loox-reviews-container'
    ];
    
    reviewDisplaySelectors.forEach(selector => {
      const elements = looxContainer.querySelectorAll(selector);
      elements.forEach(element => {
        element.style.removeProperty('display');
        element.style.setProperty('visibility', 'visible', 'important');
        element.style.setProperty('opacity', '1', 'important');
        element.removeAttribute('aria-hidden');
      });
    });
    
    // Keep display rating stars visible (in existing reviews)
    const displayStars = looxContainer.querySelectorAll(`
      .loox-review-card .loox-rating-stars,
      .loox-review-card .loox-star-rating,
      .loox-reviews-summary .loox-rating-stars,
      .loox-average-rating .loox-rating-stars
    `);
    
    displayStars.forEach(stars => {
      stars.style.setProperty('display', 'inline-block', 'important');
      stars.style.setProperty('visibility', 'visible', 'important');
      stars.style.setProperty('opacity', '1', 'important');
      stars.style.setProperty('pointer-events', 'none', 'important'); // Make them non-interactive
      stars.removeAttribute('aria-hidden');
    });
    
    if (hiddenCount > 0) {
      console.log(`Loox: Hidden ${hiddenCount} review writing elements`);
    }
  }

  /**
   * Setup periodic checks for Loox forms
   */
  setupPeriodicLooxCheck() {
    // Check every 5 seconds for the first minute after page load
    let checkCount = 0;
    const maxChecks = 12; // 12 * 5 seconds = 1 minute
    
    const periodicCheck = setInterval(() => {
      checkCount++;
      this.hideLooxReviewForms();
      
      if (checkCount >= maxChecks) {
        clearInterval(periodicCheck);
        
        // After initial checks, do less frequent checks
        setInterval(() => {
          this.hideLooxReviewForms();
        }, 30000); // Every 30 seconds
      }
    }, 5000);
  }

  /**
   * Setup event handlers for Loox-related events
   */
  setupLooxEventHandlers() {
    // Listen for common events that might trigger new content
    const events = ['load', 'DOMContentLoaded', 'resize', 'scroll'];
    
    events.forEach(eventName => {
      document.addEventListener(eventName, () => {
        this.debounce(`looxCheck-${eventName}`, () => {
          this.hideLooxReviewForms();
        }, 1000);
      });
    });
    
    // Listen for AJAX completion
    document.addEventListener('ajaxComplete', () => {
      this.hideLooxReviewForms();
    });
    
    // Listen for hash changes (in case Loox uses routing)
    window.addEventListener('hashchange', () => {
      this.hideLooxReviewForms();
    });
    
    // Listen for popstate (browser back/forward)
    window.addEventListener('popstate', () => {
      setTimeout(() => {
        this.hideLooxReviewForms();
      }, 500);
    });
    
    // Listen for focus events (in case forms are revealed on focus)
    document.addEventListener('focusin', (event) => {
      if (event.target.closest('#looxReviews')) {
        setTimeout(() => {
          this.hideLooxReviewForms();
        }, 100);
      }
    });
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new ProductPageEnhanced();
  });
} else {
  new ProductPageEnhanced();
}

// Export for external use
window.ProductPageEnhanced = ProductPageEnhanced;
